import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import Box from "@mui/material/Box";
import { type ButtonProps } from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import {
    BodyTextS,
    cs,
    HeadingS,
    Icons,
    InternalButtonLink,
    InternalLink,
    Svgs,
    useLogout,
    useTypedTranslation,
} from "@repo/ui";
import React, { type FC, type PropsWithChildren, type ReactNode } from "react";

const EmptyBar: FC<PropsWithChildren> = ({ children }) => {
    return (
        <Toolbar
            sx={{
                justifyContent: "space-between",
                height: "80px",
                px: { xs: 0 },
            }}
        >
            <InternalLink href="/" sx={{ lineHeight: 0 }}>
                <Svgs.Logo sx={{ width: 61, height: 34 }} />
            </InternalLink>
            {children}
        </Toolbar>
    );
};

export const LoggedOutBar: FC = () => {
    const { t } = useTypedTranslation();

    return (
        <EmptyBar>
            <InternalButtonLink
                href={t("appBar.contact.phoneLink")}
                customVariant="appBarLink"
                size="small"
                startIcon={<Icons.Phone sx={{ width: "1.7rem", height: "2.4rem" }} />}
            >
                {cs.appBar.contact.phone}
            </InternalButtonLink>
        </EmptyBar>
    );
};

export const MaintenanceBar: FC = () => {
    const { t } = useTypedTranslation();

    return (
        <EmptyBar>
            <InternalButtonLink
                href={t("appBar.contact.phoneLink")}
                customVariant="appBarLink"
                size="small"
                startIcon={<Icons.Phone sx={{ width: "1.7rem", height: "2.4rem" }} />}
            >
                {cs.appBar.contact.phone}
            </InternalButtonLink>
        </EmptyBar>
    );
};

const LogoutButton: FC = ({ sx }: ButtonProps) => {
    const { click } = useLogout();
    return (
        <InternalButtonLink
            customVariant="appBarLink"
            size="small"
            startIcon={<Icons.LogOut sx={{ width: "1.6rem", height: "2.1rem", mr: 2 }} />}
            sx={{ ...sx }}
            onClick={click}
            data-test="logout-button"
        >
            {cs.appBar.logout.button}
        </InternalButtonLink>
    );
};

export type BottomNavigationComponentProps = {
    BottomNavigationComponent: React.ComponentType;
};

export const LoggedInBar: FC<BottomNavigationComponentProps> = ({ BottomNavigationComponent }) => {
    return (
        <EmptyBar>
            <Box sx={{ display: { md: "none" }, flexGrow: 1 }} />
            <BottomNavigationComponent />
            <LogoutButton
                sx={{
                    pr: 0,
                }}
            />
        </EmptyBar>
    );
};

export const LogoutOnlyAppBar: FC = () => {
    return (
        <EmptyBar>
            <Box sx={{ display: { md: "none" }, flexGrow: 1 }} />
            <LogoutButton
                sx={{
                    pr: 0,
                }}
            />
        </EmptyBar>
    );
};

export const PortalAppBar: FC<BottomNavigationComponentProps> = ({ BottomNavigationComponent }) => {
    const context = useMsal();
    const isAuthenticated = useIsAuthenticated();
    if (context.inProgress !== InteractionStatus.Startup) {
        if (isAuthenticated) {
            return <LoggedInBar BottomNavigationComponent={BottomNavigationComponent} />;
        }
        return <LoggedOutBar />;
    }
    return (
        <EmptyBar>
            <Skeleton width={300} />
            <Skeleton width={100} />
        </EmptyBar>
    );
};

type BackNavigationBarProps = {
    id?: string | ReactNode;
    title: ReactNode;
    subtitle?: ReactNode;
    backHandler: () => void;
};

export const BackNavigationBar: FC<BackNavigationBarProps> = ({ id, title, subtitle, backHandler }) => {
    return (
        <Toolbar>
            <IconButton
                aria-label="menu"
                onClick={() => {
                    backHandler();
                }}
                sx={{
                    position: "relative",
                    left: (theme) => `-${theme.spacing(3)}`,
                    color: "transparent",
                }}
            >
                <Icons.Back sx={{ width: "2.2rem", height: "2.2rem" }} color="primary" />
            </IconButton>
            <Stack pt={1} width="100%">
                <HeadingS sx={{ mb: 0, color: "text.primary" }}>{title}</HeadingS>
                {subtitle && <BodyTextS sx={{ mb: -2, color: "grey.dark" }}>{subtitle}</BodyTextS>}
                {typeof id === "string" && <BodyTextS sx={{ mb: -2, color: "grey.dark" }}>{`(${id})`}</BodyTextS>}
            </Stack>
        </Toolbar>
    );
};
